import { useEffect, useState, useCallback } from "react";
import { api, BACKEND_URL } from "@/lib/api";
import { toast } from "sonner";
import { MagnifyingGlass, DownloadSimple, EnvelopeSimple } from "@phosphor-icons/react";

const when = (v) => (v ? String(v).slice(0, 16).replace("T", " ") : "—");

const CAMPAIGNS = [["", "Every mail"], ["migration", "Migration email"],
  ["migration_chase", "Migration reminder"], ["farewell", "Sorry to see you go"]];

const Tile = ({ label, value }) => (
  <div className="p-4 border border-zinc-800 bg-[#121214]">
    <div className="text-[10px] uppercase tracking-widest text-zinc-500">{label}</div>
    <div className="text-2xl font-bold mt-1 font-mono-data" data-testid={`mail-tile-${label.toLowerCase().replace(/ /g, "-")}`}>{value ?? 0}</div>
  </div>
);

export default function MailHistoryPage() {
  const [batches, setBatches] = useState([]);
  const [totals, setTotals] = useState({});
  const [people, setPeople] = useState([]);
  const [shown, setShown] = useState({ total_people: 0, shown: 0 });
  const [campaign, setCampaign] = useState("");
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(null);
  const [loading, setLoading] = useState(true);
  const [alerts, setAlerts] = useState([]);
  const [syncing, setSyncing] = useState(false);

  const loadPeople = useCallback(async (c, query) => {
    setLoading(true);
    try {
      const { data } = await api.get("/reports/mail-history/people", { params: { campaign: c, q: query } });
      setPeople(data.people); setShown({ total_people: data.total_people, shown: data.shown });
    } catch (e) { toast.error("Couldn't load the mail history"); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => {
    api.get("/reports/mail-history").then((r) => { setBatches(r.data.batches); setTotals(r.data.totals); }).catch(() => {});
    api.get("/communications/duplicate-alerts").then((r) => setAlerts(r.data.alerts || [])).catch(() => {});
    loadPeople("", "");
  }, [loadPeople]);

  const syncResend = async () => {
    setSyncing(true);
    try {
      const { data } = await api.post("/reports/mail-history/sync-resend");
      toast.success(`Pulled ${data.added} sends from Resend`, {
        description: `${data.looked_at} checked · ${data.refreshed} already recorded · now covering ${data.people} people`,
      });
      const r = await api.get("/reports/mail-history");
      setBatches(r.data.batches); setTotals(r.data.totals);
      loadPeople(campaign, q);
    } catch (e) { toast.error("Couldn't reach Resend", { description: e.response?.data?.detail || e.message }); }
    finally { setSyncing(false); }
  };

  const pick = (c) => { setCampaign(c); loadPeople(c, q); };
  const search = (e) => { e.preventDefault(); loadPeople(campaign, q); };

  return (
    <div className="p-8 max-w-6xl" data-testid="mail-history-page">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Mail history</h1>
          <p className="text-sm text-zinc-400 mt-1 max-w-2xl">
            Every batch we have sent to members and every person in it — which mail, which batch, the
            date and time, and how many times they have been mailed. Pulled from Resend itself, so it
            survives even when the subscribers are deleted afterwards.
          </p>
        </div>
        <button onClick={syncResend} disabled={syncing} className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white shrink-0 disabled:opacity-40" data-testid="mail-sync-resend">
          {syncing ? "Pulling from Resend…" : "Sync from Resend"}
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-3 mt-6">
        <Tile label="Batches sent" value={totals.batches} />
        <Tile label="Emails sent" value={totals.emails} />
        <Tile label="People mailed" value={totals.people} />
      </div>

      {alerts.length > 0 && (
        <div className="mt-6 border border-red-900/60 bg-red-500/10 p-4" data-testid="duplicate-alerts">
          <div className="text-[10px] uppercase tracking-widest text-red-400 font-bold">Duplicate sends</div>
          {alerts.map((a) => (
            <div key={a.id} className="text-[12.5px] text-zinc-200 mt-2 leading-relaxed" data-testid={`duplicate-alert-${a.id}`}>
              <b>{when(a.at)}</b> — {a.by} re-sent <b>{a.campaign}</b> to {a.people} people who had already had it
              {a.confirmed_delivered ? ` (${a.confirmed_delivered} of them previously confirmed delivered)` : ""}.
            </div>
          ))}
        </div>
      )}

      <div className="mt-8 text-[10px] uppercase tracking-widest text-zinc-500">Batches</div>
      <div className="mt-2 border border-zinc-800" data-testid="mail-batches">
        {batches.length === 0 && <div className="p-6 text-sm text-zinc-500 bg-[#121214]">No member mail has gone out yet.</div>}
        {batches.map((b) => (
          <div key={b.campaign_id} className="p-4 border-b border-zinc-800 last:border-0 bg-[#121214]" data-testid={`mail-batch-${b.campaign_id}`}>
            <div className="flex items-start justify-between gap-4 flex-wrap">
              <div className="min-w-0">
                <div className="font-semibold flex items-center gap-2">
                  <EnvelopeSimple size={15} className="text-zinc-500 shrink-0" />
                  {b.label}
                  {b.batch && <span className="text-[10px] uppercase tracking-wider text-zinc-400 border border-zinc-700 px-2 py-[2px] rounded-full">{b.batch}</span>}
                </div>
                <div className="text-[12px] text-zinc-400 mt-1 truncate">{b.subject}</div>
              </div>
              <div className="font-mono-data text-[11px] text-zinc-400 text-right shrink-0">
                <div data-testid={`mail-batch-when-${b.campaign_id}`}>{when(b.sent_at)}</div>
                <div>{b.sent_by || "—"}</div>
              </div>
            </div>
            <div className="font-mono-data text-[11px] text-zinc-300 mt-2">
              {b.people} recipients · {b.sent} sent
              {b.delivered ? ` · ${b.delivered} delivered` : ""}{b.opened ? ` · ${b.opened} opened` : ""}
              {b.bounced ? ` · ${b.bounced} bounced` : ""}{b.blocked ? ` · ${b.blocked} blocked` : ""}
              {b.failed ? ` · ${b.failed} failed` : ""}
              {b.last_at !== b.sent_at ? ` · finished ${when(b.last_at)}` : ""}
            </div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex items-end justify-between gap-4 flex-wrap">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">People</div>
          <div className="text-sm text-zinc-400 mt-1" data-testid="mail-people-count">
            {shown.shown} of {shown.total_people} people{campaign ? ` mailed in ${CAMPAIGNS.find((c) => c[0] === campaign)?.[1]}` : ""}
          </div>
        </div>
        <a href={`${BACKEND_URL}/api/reports/mail-history/export.csv${campaign ? `?campaign=${campaign}` : ""}`}
          className="btn-tactical bg-zinc-800 hover:bg-zinc-700 text-white flex items-center gap-2 no-underline" data-testid="mail-export-csv">
          <DownloadSimple size={14} /> Export CSV
        </a>
      </div>

      <div className="flex gap-2 mt-4 flex-wrap">
        {CAMPAIGNS.map(([v, l]) => (
          <button key={v || "all"} onClick={() => pick(v)} data-testid={`mail-filter-${v || "all"}`}
            className={`px-3 py-1.5 text-xs font-semibold border rounded-full transition-colors ${campaign === v ? "border-[#007AFF] bg-[#007AFF] text-white" : "border-zinc-800 text-zinc-400 hover:text-zinc-200"}`}>
            {l}
          </button>
        ))}
      </div>

      <form onSubmit={search} className="flex gap-2 mt-4">
        <div className="relative flex-1">
          <MagnifyingGlass size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a name or email address"
            className="tactical-input w-full pl-9" data-testid="mail-search-input" />
        </div>
        <button type="submit" className="btn-tactical bg-[#007AFF] hover:bg-[#005ecb] text-white" data-testid="mail-search-btn">Search</button>
      </form>

      <div className="mt-4 border border-zinc-800" data-testid="mail-people-list">
        {loading && <div className="p-6 text-sm text-zinc-500 bg-[#121214]">Loading…</div>}
        {!loading && people.length === 0 && <div className="p-6 text-sm text-zinc-500 bg-[#121214]">Nobody matches that.</div>}
        {!loading && people.map((p) => (
          <div key={p.email} className="border-b border-zinc-800 last:border-0 bg-[#121214]" data-testid={`mail-person-${p.email}`}>
            <button onClick={() => setOpen(open === p.email ? null : p.email)}
              className="w-full text-left p-4 hover:bg-zinc-900 transition-colors">
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="min-w-0">
                  <div className="font-semibold flex items-center gap-2 flex-wrap">
                    {p.name || p.email}
                    {p.times_opened > 0 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#4dabff] border border-[#007AFF]/50 px-2 py-[2px] rounded-full" data-testid={`mail-person-read-${p.email}`}>
                        read {p.times_opened > 1 ? `${p.times_opened}×` : ""}
                      </span>
                    )}
                    {p.times_clicked > 0 && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-violet-300 border border-violet-800 px-2 py-[2px] rounded-full" data-testid={`mail-person-clicked-${p.email}`}>
                        clicked the link
                      </span>
                    )}
                    {p.on_app && <span className="text-[10px] font-bold uppercase tracking-wider text-green-400 border border-green-900 px-2 py-[2px] rounded-full">on the app</span>}
                    {p.bounced && <span className="text-[10px] font-bold uppercase tracking-wider text-red-400 border border-red-900 px-2 py-[2px] rounded-full">bounced</span>}
                    {p.opted_out && <span className="text-[10px] font-bold uppercase tracking-wider text-zinc-400 border border-zinc-700 px-2 py-[2px] rounded-full">opted out</span>}
                  </div>
                  <div className="font-mono-data text-[11px] text-zinc-400 mt-1 truncate">{p.email}</div>
                </div>
                <div className="font-mono-data text-[11px] text-zinc-300 text-right shrink-0">
                  <div data-testid={`mail-person-times-${p.email}`}>mailed {p.times_mailed}×</div>
                  <div className="text-zinc-500">last {when(p.last_at)}</div>
                </div>
              </div>
            </button>
            {open === p.email && (
              <div className="px-4 pb-4" data-testid={`mail-person-trail-${p.email}`}>
                {p.sends.map((s, i) => (
                  <div key={i} className="flex items-center justify-between gap-3 py-1.5 border-t border-zinc-800/60 text-[12px]">
                    <span className="text-zinc-200">{s.label}</span>
                    <span className="font-mono-data text-[11px] text-zinc-400">
                      {when(s.at)} · {s.status}
                      {s.last_event && s.last_event !== s.status ? ` → ${s.last_event}` : ""}
                      {s.clicked_at ? ` · clicked ${when(s.clicked_at)}` : s.opened_at ? ` · read ${when(s.opened_at)}` : ""}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
